import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { ActionFab } from '../../../components/ActionButton'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { formatCurrency } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IContestApprovedArtwork } from '../../../services/Contests/interfaces'
import { resolveArtMeta } from '../Contests/artFieldHelpers'
import './ContestsLabelsPage.css'

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

export default function ContestsLabelsPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const contestsService = useMemo(() => new ContestsService(), [])
  const passedArtworks = (location.state as { artworks?: IContestApprovedArtwork[] })?.artworks

  const [artworks, setArtworks] = useState<IContestApprovedArtwork[]>(passedArtworks || [])
  const [loading, setLoading] = useState(!passedArtworks)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // If artworks were passed via state, skip fetching
    if (passedArtworks && passedArtworks.length >= 0) {
      setArtworks(passedArtworks)
      setLoading(false)
      return
    }

    if (!slug) {
      setLoading(false)
      return
    }

    contestsService
      .getBySlug(slug)
      .then((contest) => contestsService.listApprovedArtworks(contest.id))
      .then((arts) => {
        setArtworks(arts)
      })
      .catch(() => {
        setError('Não foi possível carregar as etiquetas.')
      })
      .finally(() => setLoading(false))
  }, [slug, contestsService, passedArtworks])

  useEffect(() => {
    if (!loading && !error && artworks.length > 0) {
      const timer = setTimeout(() => {
        window.print()
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [loading, error, artworks.length])

  if (loading) {
    return <LoadingBlock size="page" entity="etiquetas" />
  }

  if (error) {
    return (
      <div className="labels-loading">
        <p>{error}</p>
      </div>
    )
  }

  if (artworks.length === 0) {
    return (
      <div className="labels-loading">
        <p>Nenhuma obra aprovada para esse concurso.</p>
      </div>
    )
  }

  const pages = chunk(artworks, 4)

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="labels-wrapper">
      <PageTitle title="Etiquetas de Obras" description="Folhas de etiquetas prontas para impressão" />

      <div className="labels-document">
        {pages.map((pageArtworks, pageIndex) => (
          <div key={pageIndex} className={`labels-sheet${pageIndex < pages.length - 1 ? ' page-break' : ''}`}>
            {Array.from({ length: 4 }, (_, i) => {
              const artwork: IContestApprovedArtwork | undefined = pageArtworks[i]

              if (!artwork) {
                return <div key={`empty-${i}`} className="label label-empty" />
              }

              return (
                <div
                  key={artwork.id}
                  className="label"
                  style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/img/label-background.png)` }}
                >
                  <div className="label_content">
                    <div className="label_top">
                      <div className="label_title">{artwork.name}</div>
                      <div className="label_artist">{artwork.applicant_name ?? ''}</div>
                    </div>

                    <div className="label_bottom">
                      {artwork.available_for_auction && (
                        <div className="label_bid">
                          <span className="label_bid-label">Lance Inicial:</span>
                          <span className="label_bid-value">{formatCurrency(artwork.starting_bid)}</span>
                        </div>
                      )}
                      <div className="label_details">
                        {resolveArtMeta(artwork)
                          .filter((meta) => meta.show_in_labels && meta.answered)
                          .slice(0, 2)
                          .map((meta) => (
                            <div key={meta.key}>
                              <strong>{meta.label}:</strong> {meta.value}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <ActionFab
        action="download"
        label="Emitir etiquetas de obras"
        colorScheme="magenta"
        icon={<Icon name="lni lni-printer" />}
        onClick={handlePrint}
      />
    </div>
  )
}
